import ListView from '@/components/datagrid/ListView';
import useApiQuery from '@/hooks/useApiQuery';
import useLiveData from '@/hooks/useLiveData';
import useTenantQuery from '@/hooks/useTenantQuery';
import useCrmStore from '@/store/crm';
import { KeyboardArrowDown } from '@mui/icons-material';
import ViewListOutlinedIcon from '@mui/icons-material/ViewListOutlined';
import InsertChartOutlinedIcon from '@mui/icons-material/InsertChartOutlined';
import {
  Avatar,
  Box,
  Button,
  Card,
  Menu,
  MenuItem,
  Typography,
  Tooltip,
  Skeleton,
  useTheme,
  Divider,
  LinearProgress,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { Link, useRouteLoaderData } from 'react-router-dom';
import { BarChart } from '@mui/x-charts';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import GroupsIcon from '@mui/icons-material/Groups';
import { Stack } from '@mui/system';
import { useOutletContext } from 'react-router-dom';
import GroupCalllistDetails from '@/components/forms/calls/GroupCalllistDetails';

export default function Callcenter() {
  const { users } = useRouteLoaderData('auth');
  const { data: teams } = useTenantQuery(`team`);

  const { reports, report, setReport, loading } = useOutletContext();

  const theme = useTheme();
  const [view, setView] = useState('userlist');

  const {
    range,
    setRange,
    group,
    setGroup,
    team,
    setTeam,
    setFilter,
    setSort,
  } = useCrmStore();
  useEffect(() => {
    setFilter(null);
  }, []);

  const [groups, setGroups] = useState([]);
  const [groupAnalysis, setGroupAnalysis] = useState([]);

  const [chartData, setChartData] = useState({
    days: [],
    missed: [],
    incoming: [],
  });

  const startMidnight = new Date(range.startDate);
  startMidnight.setHours(0, 0, 0, 0);
  const endMidnight = new Date(range.endDate);
  endMidnight.setHours(0, 0, 0, 0);

  const isSingleDay = startMidnight.getTime() === endMidnight.getTime();

  async function fetchGroupsAnalysis() {
    if (!report) {
      setGroupAnalysis([]);
      return;
    }

    const groupsAnalysis = await Promise.all(
      report.data.groups.map(async (group, index) => {
        return {
          _id: `${group.accountId}_${index}`,
          ...group,
          incomingCalls: group.calls.ringing || 0,
          missedCalls: group.calls.missed || 0,
          avgWaitTime: `${group.calls.averageWaitingTimeSeconds || 0}s`,
          waitingMissed: `${(group.calls.averageMissedWaitingTimeSeconds || 0).toFixed(2)}s`,
          outgoingCalls: group.calls.outgoing || 0,
        };
      }),
    );

    setGroupAnalysis(groupsAnalysis);
  }

  async function fetchCallChartData() {
    if (!reports || reports.length === 0 || !report) {
      setChartData({ days: [], missed: [], incoming: [] });
      return;
    }

    const dailyReports = reports.filter(
      (rpt) =>
        rpt.interval === 'DAILY' &&
        new Date(rpt.timeStart) >= new Date(report.timeStart) &&
        new Date(rpt.timeEnd) <= new Date(report.timeEnd),
    );

    const chartData = { days: [], missed: [], incoming: [] };

    if (dailyReports.length === 1) {
      const newDays = dailyReports[0].data.stats.callsPerHour.map(
        (_, hour) => `${hour}:00`,
      );
      chartData.days = newDays;
      if (group) {
        const selectedGroup = dailyReports[0].data.groups.find(
          (grp) => grp.accountId === group.accountId,
        );
        if (selectedGroup && team) {
          const teamMembers = users.filter(
            (user) =>
              team.members.includes(user._id) ||
              team.managers.includes(user._id),
          );

          const reportTeamUsers = report.data.users.filter(
            (user) =>
              user._id &&
              teamMembers.find((tm) => tm._id === user._id) &&
              user.groups.includes(selectedGroup.name),
          );

          reportTeamUsers.forEach((user) => {
            user.callsPerHour.forEach((calls, hour) => {
              if (!chartData.missed[hour]) chartData.missed[hour] = 0;
              if (!chartData.incoming[hour]) chartData.incoming[hour] = 0;
              chartData.missed[hour] += calls.missedCalls || 0;
              chartData.incoming[hour] += calls.incomingCalls || 0;
            });
          });
          setChartData(chartData);
        } else if (selectedGroup) {
          selectedGroup.callsPerHour.forEach((calls) => {
            chartData.missed.push(calls.missedCalls || 0);
            chartData.incoming.push(calls.incomingCalls || 0);
          });
        }
      } else if (team) {
        const teamMembers = users.filter(
          (user) =>
            team.members.includes(user._id) || team.managers.includes(user._id),
        );

        const reportTeamUsers = report.data.users.filter(
          (user) => user._id && teamMembers.find((tm) => tm._id === user._id),
        );

        reportTeamUsers.forEach((user) => {
          user.callsPerHour.forEach((calls, hour) => {
            if (!chartData.missed[hour]) chartData.missed[hour] = 0;
            if (!chartData.incoming[hour]) chartData.incoming[hour] = 0;
            chartData.missed[hour] += calls.missedCalls || 0;
            chartData.incoming[hour] += calls.incomingCalls || 0;
          });
        });
        setChartData(chartData);
      } else {
        dailyReports[0].data.stats.callsPerHour.forEach((calls) => {
          chartData.missed.push(calls.missedCalls || 0);
          chartData.incoming.push(calls.incomingCalls || 0);
        });
      }

      setChartData(chartData);
      return;
    }

    dailyReports.forEach((report) => {
      chartData.days.push(
        `${new Date(report.timeStart).getDate()}.${(
          new Date(report.timeStart).getMonth() + 1
        )
          .toString()
          .padStart(2, '0')}.${new Date(report.timeStart).getFullYear()}`,
      );

      if (group) {
        if (team) {
          let teamMissed = 0;
          let teamIncoming = 0;

          const teamMembers = users.filter(
            (user) =>
              team.members.includes(user._id) ||
              team.managers.includes(user._id),
          );

          const reportTeamUsers = report.data.users.filter(
            (user) =>
              user._id &&
              teamMembers.find((tm) => tm._id === user._id) &&
              user.groups.includes(group.name),
          );

          reportTeamUsers.forEach((user) => {
            teamMissed += user.calls.missed || 0;
            teamIncoming += user.calls.ringing || 0;
          });

          chartData.missed.push(teamMissed);
          chartData.incoming.push(teamIncoming);
          setChartData(chartData);
          return;
        }
        const selectedGroup = report.data.groups.find(
          (g) => g.accountId === group.accountId,
        );
        if (selectedGroup) {
          chartData.missed.push(selectedGroup.calls.missed || 0);
          chartData.incoming.push(selectedGroup.calls.ringing || 0);
        }
      } else if (team) {
        let teamMissed = 0;
        let teamIncoming = 0;

        const teamMembers = users.filter(
          (user) =>
            team.members.includes(user._id) || team.managers.includes(user._id),
        );

        const reportTeamUsers = report.data.users.filter(
          (user) => user._id && teamMembers.find((tm) => tm._id === user._id),
        );

        reportTeamUsers.forEach((user) => {
          teamMissed += user.calls.missed || 0;
          teamIncoming += user.calls.ringing || 0;
        });

        chartData.missed.push(teamMissed);
        chartData.incoming.push(teamIncoming);
      } else {
        chartData.missed.push(report.data.stats.missedCalls || 0);
        chartData.incoming.push(report.data.stats.incomingCalls || 0);
      }
    });
    setChartData(chartData);
  }

  useEffect(() => {
    if (view === 'group') {
      fetchGroupsAnalysis();
    }
  }, [report, view]);

  useEffect(() => {
    if (view === 'chart') {
      fetchCallChartData();
    }
  }, [report, view, group, team]);

  const [reportSelector, setReportSelector] = useState(null);
  const [teamSelector, setTeamSelector] = useState(null);
  const [groupSelector, setGroupSelector] = useState(null);

  const { data: unfilteredCallbacks, refetch: refetchCallbacks } = useApiQuery(
    `callback`,
    '',
    true,
  );

  useLiveData('call');

  useEffect(() => {
    refetchCallbacks({
      $and: [
        { createdAt: { $lt: new Date(range.endDate) } },
        { completedAt: { $gt: new Date(range.endDate) } },
      ],
    });
  }, [range]);

  // eslint-disable-next-line no-unused-vars
  const callbacks = useMemo(() => {
    return unfilteredCallbacks?.filter((callback) => {
      return (
        new Date(callback.createdAt) < range.endDate &&
        (!callback.completedAt ||
          new Date(callback.completedAt) > range.endDate)
      );
    });
  }, [unfilteredCallbacks, range]);

  const [incoming, setIncoming] = useState(null);
  const [missed, setMissed] = useState(null);
  const [waiting, setWaiting] = useState(null);
  const [waitingMissed, setWaitingMissed] = useState(null);
  const [outgoing, setOutgoing] = useState(null);
  const [userStats, setUserStats] = useState([]);

  useEffect(() => {
    if (group || team) {
      const selectedGroup = report?.data.groups.find(
        (g) => g.accountId === group?.accountId,
      );

      if (selectedGroup && team) {
        const teamMembers = users.filter(
          (user) =>
            team.members.includes(user._id) || team.managers.includes(user._id),
        );

        const reportTeamUsers = report.data.users.filter(
          (user) =>
            user._id &&
            teamMembers.find((tm) => tm._id === user._id) &&
            user.groups.includes(selectedGroup.name),
        );

        let teamIncoming = 0;
        let teamMissed = 0;
        let totalWaitingTime = 0;
        let totalMissedWaitingTime = 0;
        let totalAnsweredCalls = 0;

        reportTeamUsers.forEach((user) => {
          teamIncoming += user.calls.ringing || 0;
          teamMissed += user.calls.missed || 0;
          totalWaitingTime +=
            (user.calls.averageWaitingTimeSeconds || 0) *
            ((user.calls.answered || 0) + (user.calls.missed || 0));
          totalMissedWaitingTime +=
            (user.calls.averageMissedWaitingTimeSeconds || 0) *
            (user.calls.missed || 0);
          totalAnsweredCalls += user.calls.answered || 0;
        });

        setIncoming(teamIncoming);
        setMissed(teamMissed);
        setWaiting(
          totalAnsweredCalls > 0 ? totalWaitingTime / totalAnsweredCalls : 0,
        );
        setWaitingMissed(
          teamMissed > 0 ? totalMissedWaitingTime / teamMissed : 0,
        );
        setOutgoing(0);
        return;
      } else if (team) {
        setIncoming(report?.data.stats.incomingCalls);
        setMissed(report?.data.stats.missedCalls);
        setWaiting(report?.data.stats.averageWaitingTimeSeconds);
        setWaitingMissed(report?.data.stats.averageMissedWaitingTimeSeconds);
        setOutgoing(report?.data.stats.outgoingCalls);
        return;
      } else if (selectedGroup) {
        setIncoming(selectedGroup.calls.ringing);
        setMissed(selectedGroup.calls.missed);
        setWaiting(selectedGroup.calls.averageWaitingTimeSeconds);
        setWaitingMissed(selectedGroup.calls.averageMissedWaitingTimeSeconds);
        setOutgoing(0);
      }
    } else {
      setIncoming(report?.data.stats.incomingCalls);
      setMissed(report?.data.stats.missedCalls);
      setWaiting(report?.data.stats.averageWaitingTimeSeconds);
      setWaitingMissed(report?.data.stats.averageMissedWaitingTimeSeconds);
      setOutgoing(report?.data.stats.outgoingCalls);
    }
  }, [group, team]);

  useEffect(() => {
    if (report) {
      const groups = report?.data.groups.map((group) => ({
        accountId: group.accountId,
        name: group.name,
      }));

      groups.sort((a, b) => a.name.localeCompare(b.name));

      setIncoming(report?.data.stats.incomingCalls);
      setMissed(report?.data.stats.missedCalls);
      setWaiting(report?.data.stats.averageWaitingTimeSeconds);
      setWaitingMissed(report?.data.stats.averageMissedWaitingTimeSeconds);
      setOutgoing(report?.data.stats.outgoingCalls);
      setGroups(groups);

      const range = {
        startDate: new Date(report.timeStart),
        endDate: new Date(report.timeEnd),
      };

      setRange(range);

      setUserStats(
        report?.data.users.map((user) => {
          const dbUser = users.find((u) => u._id === user._id);
          return {
            _id: user._id,
            firstName: dbUser?.firstName || '',
            lastName: dbUser?.lastName || '',
            phone: dbUser?.phone || '',
            starfaceId: dbUser?.starfaceId || '',
            profilePicture: dbUser?.profilePicture || '',
            totalDuration: user?.calls?.totalDurationIncoming || 0,
            maxDuration: user?.calls?.maxDurationIncomingSeconds || 0,
            avgDuration: user?.calls?.averageDurationIncomingSeconds || 0,
            ringing: user?.calls?.ringing || 0,
            pickup: user?.calls?.answered || 0,
            outgoing: user?.calls?.outgoing || 0,
            missed: user?.calls?.missed || 0,
          };
        }),
      );
    } else {
      setGroups([]);
      setGroup(null);
      setUserStats([]);
      setIncoming(null);
      setMissed(null);
      setWaiting(null);
      setWaitingMissed(null);
      setOutgoing(null);
      setGroups([]);
    }
  }, [report]);

  useEffect(() => {
    if (reports && reports.length > 0) {
      if (!report) {
        const hourlyReports = reports.filter((rpt) => {
          return rpt.interval === 'HOURLY';
        });

        if (hourlyReports.length > 0) {
          const latestReport = hourlyReports.reduce((latest, current) => {
            return new Date(current.timeStart) > new Date(latest.timeStart)
              ? current
              : latest;
          }, hourlyReports[0]);

          setReport(latestReport);
        }
      }
    }
  }, [reports]);

  const intervalLabels = {
    DAILY: 'Tag',
    HOURLY: 'Laufender Tag',
    WEEKLY: 'Woche',
    MONTHLY: 'Monat',
  };

  function getWeekNumber(date) {
    const d = new Date(
      Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()),
    );
    d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    const weekNo = Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
    return weekNo;
  }

  function formatMenuItemLabel(report) {
    const start = new Date(report.timeStart);
    const end = new Date(report.timeEnd);

    switch (report.interval) {
      case 'HOURLY':
        return `${start.toLocaleDateString('de-DE')} ${start.getHours()}:00 - ${end.getHours()}:00`;
      case 'DAILY':
        return `${start.toLocaleDateString('de-DE')}`;
      case 'WEEKLY':
        return `KW ${getWeekNumber(start)} (${start.toLocaleDateString('de-DE')} - ${end.toLocaleDateString('de-DE')})`;
      case 'MONTHLY':
        return `${start.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })}`;
      default:
        return `${start.toLocaleDateString('de-DE')} - ${end.toLocaleDateString('de-DE')}`;
    }
  }

  const groupedReports =
    reports?.reduce((acc, report) => {
      if (!acc[report.interval]) acc[report.interval] = [];
      acc[report.interval].push(report);
      return acc;
    }, {}) || [];

  const columns = [
    {
      field: '_id',
      headerName: 'Benutzer',
      align: 'center',
      flex: 1,
      valueGetter: (params) => {
        const user = users?.find((u) => u._id === params);
        return `${user?.firstName} ${user?.lastName}`;
      },
      renderCell: ({ row }) => {
        return (
          <Box
            sx={{
              display: 'flex',
              flexFlow: 'row',
              alignItems: 'center',
              height: '100%',
            }}
          >
            <Avatar
              src={
                row.profilePicture &&
                `${import.meta.env.VITE_FILE_URL}/${row.profilePicture}`
              }
              sx={{ width: '32px', height: '32px', mr: 1 }}
            />
            <Box
              sx={{
                display: 'flex',
                flexFlow: 'column',
                justifyContent: 'center',
                alignItems: 'flex-start',
              }}
            >
              <Typography variant="body1">
                {`${row.firstName} ${row.lastName}`}
              </Typography>
              <Typography variant="subtitle2">{row.phone}</Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      field: 'totalDuration',
      headerName: 'Gesamtdauer',
      align: 'center',
      headerAlign: 'center',
      flex: 0.5,
      valueGetter: (value) => (!value || isNaN(value) ? 0 : Number(value)),
      renderCell: (params) => {
        const totalSeconds = params.value;
        if (
          totalSeconds === null ||
          totalSeconds === undefined ||
          isNaN(totalSeconds)
        ) {
          return '00:00:00 h';
        }

        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = Math.floor(totalSeconds % 60);

        if (hours === 0) {
          return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')} min`;
        } else {
          return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')} h`;
        }
      },
    },
    {
      field: 'maxDuration',
      headerName: 'Max. Dauer',
      align: 'center',
      headerAlign: 'center',
      flex: 0.5,
      valueGetter: (value) => (!value || isNaN(value) ? 0 : Number(value)),
      renderCell: (params) => {
        const totalSeconds = params.value;
        if (
          totalSeconds === null ||
          totalSeconds === undefined ||
          isNaN(totalSeconds)
        ) {
          return '00:00:00 h';
        }

        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = Math.floor(totalSeconds % 60);

        if (hours === 0) {
          return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')} min`;
        } else {
          return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')} h`;
        }
      },
    },
    {
      field: 'avgDuration',
      headerName: 'Ø Dauer',
      align: 'center',
      headerAlign: 'center',
      flex: 0.5,
      valueGetter: (value) => (!value || isNaN(value) ? 0 : Number(value)),
      renderCell: (params) => {
        const totalSeconds = params.value;
        if (
          totalSeconds === null ||
          totalSeconds === undefined ||
          isNaN(totalSeconds)
        ) {
          return '00:00:00 h';
        }

        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = Math.floor(totalSeconds % 60);

        if (hours === 0) {
          return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')} min`;
        } else {
          return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')} h`;
        }
      },
    },
    {
      field: 'ringing',
      headerName: 'Geklingelt',
      align: 'center',
      headerAlign: 'center',
      flex: 0.4,
      type: 'number',
      valueGetter: (value, row) => row?.ringing || 0,
      renderCell: ({ row }) => {
        return (
          <Link
            to={`/crm/callcenter/calls`}
            style={{ textDecoration: 'none', color: 'inherit' }}
            onClick={() => {
              setFilter({
                ringingUsers: row.starfaceId,
                outgoing: false,
                ringingGroups: group ? group.accountId : undefined,
              });
            }}
          >
            {row.ringing || 0}
          </Link>
        );
      },
    },
    {
      field: 'pickup',
      headerName: 'Abgehoben',
      align: 'center',
      headerAlign: 'center',
      flex: 0.4,
      type: 'number',
      valueGetter: (value, row) => row?.pickup || 0,
      renderCell: ({ row }) => {
        return (
          <Link
            to={`/crm/callcenter/calls`}
            style={{ textDecoration: 'none', color: 'inherit' }}
            onClick={() =>
              setFilter({
                pickupUsers: row.starfaceId,
                outgoing: false,
                pickup: true,
                ringingGroups: group ? group.accountId : undefined,
              })
            }
          >
            {row.pickup || 0}
          </Link>
        );
      },
    },
    {
      field: 'outgoing',
      headerName: 'Ausgehend',
      align: 'center',
      headerAlign: 'center',
      flex: 0.4,
      type: 'number',
      valueGetter: (value, row) => row?.outgoing || 0,
      renderCell: ({ row }) => {
        return (
          <Link
            to={`/crm/callcenter/calls`}
            style={{ textDecoration: 'none', color: 'inherit' }}
            onClick={() =>
              setFilter({
                callerAccountId: row.starfaceId,
                outgoing: true,
                ringingGroups: group ? group.accountId : undefined,
              })
            }
          >
            {row.outgoing || 0}
          </Link>
        );
      },
    },
    {
      field: 'missed',
      headerName: 'Verpasst',
      align: 'center',
      headerAlign: 'center',
      flex: 0.4,
      type: 'number',
      valueGetter: (value, row) => row?.missed || 0,
      renderCell: ({ row }) => {
        return (
          <Link
            to={`/crm/callcenter/calls`}
            style={{ textDecoration: 'none', color: 'inherit' }}
            onClick={() =>
              setFilter({
                ringingUsers: row.starfaceId,
                outgoing: false,
                pickup: false,
                ringingGroups: group ? group.accountId : undefined,
              })
            }
          >
            {row.missed || 0}
          </Link>
        );
      },
    },
  ];

  const groupColumns = [
    {
      field: 'name',
      headerName: 'Gruppe',
      headerAlign: 'left',
      flex: 1,
      renderCell: ({ row }) => {
        return (
          <Box
            sx={{
              display: 'flex',
              flexFlow: 'row',
              alignItems: 'center',
              height: '100%',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexFlow: 'column',
                justifyContent: 'center',
                alignItems: 'flex-start',
              }}
            >
              <Typography
                variant="body1"
                fontWeight={'bold'}
              >{`${row.name}`}</Typography>
              <Typography variant="subtitle2">{`Nr: ${row.accountId}`}</Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      field: 'incomingCalls',
      headerName: 'Eingehende Anrufe',
      headerAlign: 'center',
      align: 'center',
      flex: 0.5,
      type: 'number',
    },
    {
      field: 'missedCalls',
      headerName: 'Verpasste Anrufe',
      headerAlign: 'center',
      align: 'center',
      flex: 0.5,
      type: 'number',
    },
    {
      field: 'avgWaitTime',
      headerName: 'Ø Wartezeit',
      headerAlign: 'center',
      align: 'center',
      flex: 0.5,
      type: 'number',
    },
    {
      field: 'waitingMissed',
      headerName: 'Ø Geduld bis Abbruch',
      headerAlign: 'center',
      align: 'center',
      flex: 0.5,
      type: 'number',
    },
  ];

  if (!loading && reports && reports.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '93vh',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Card sx={{ p: 4, minWidth: 320, textAlign: 'center' }}>
          <InsertChartOutlinedIcon
            sx={{ fontSize: 40, color: theme.palette.text.secondary, mb: 2 }}
          />
          <Typography variant="h6" gutterBottom>
            Noch keine Reportdaten verfügbar
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Es wurden noch keine Callcenter-Reports erstellt.<br />
            Bitte prüfen Sie die Report-Einstellungen im Callcenter-Modul.
          </Typography>
        </Card>
      </Box>
    );
  }

  if (loading || !report || !report.data) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '93vh',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Card sx={{ p: 4, minWidth: 320, textAlign: 'center' }}>
          <LinearProgress sx={{ mb: 2 }} variant='indeterminate' />
          <Typography variant="h6" gutterBottom>
            Reportdaten werden geladen …
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Bitte warten Sie einen Moment, bis die aktuellen Callcenter-Reports verfügbar sind.
          </Typography>
        </Card>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '93vh',
      }}
    >
      <Box sx={{ p: 2, pb: 0 }}>
        <Card
          sx={{
            py: 1,
            px: 2,
            gap: 2,
            display: 'flex',
            flexDirection: 'row',
          }}
        >
          <Button
            disableElevation
            onClick={(evt) => setReportSelector(evt.currentTarget)}
            endIcon={<KeyboardArrowDown />}
            ref={reportSelector}
            sx={{ width: report ? 'auto' : '250px' }}
          >
            <Typography noWrap>
              {report ? formatMenuItemLabel(report) : 'Kein Report ausgewählt'}
            </Typography>
          </Button>

          <Menu
            anchorEl={reportSelector}
            open={!!reportSelector}
            onClose={() => setReportSelector(null)}
            onClick={(evt) => evt.stopPropagation()}
            sx={{ minWidth: '200px', minHeight: '200px', maxHeight: '800px' }}
          >
            {Object.entries(groupedReports).map(
              ([interval, group], idx, arr) => (
                <Box key={interval}>
                  <MenuItem disabled sx={{ fontWeight: 'bold', opacity: 1 }}>
                    {intervalLabels[interval] || interval}
                  </MenuItem>
                  {group.map((report) => (
                    <MenuItem
                      key={report.accountId}
                      onClick={() => {
                        setReport(report);
                        setReportSelector(null);
                      }}
                    >
                      {formatMenuItemLabel(report)}
                    </MenuItem>
                  ))}
                  {idx < arr.length - 1 && <Divider />}
                </Box>
              ),
            )}
          </Menu>
          {view !== 'group' && (
            <Button
              disableElevation
              onClick={(evt) => setGroupSelector(evt.currentTarget)}
              endIcon={<KeyboardArrowDown />}
              ref={groupSelector}
              sx={{ width: '160px' }}
            >
              <Typography noWrap>{group?.name || 'Alle Gruppen'}</Typography>
            </Button>
          )}
          <Menu
            anchorEl={groupSelector}
            open={!!groupSelector}
            onClose={() => setGroupSelector(null)}
            onClick={(evt) => evt.stopPropagation()}
            sx={{ minWidth: '200px', minHeight: '200px' }}
          >
            <MenuItem
              onClick={() => {
                setGroup(null);
                setGroupSelector(null);
              }}
            >
              Alle Gruppen
            </MenuItem>
            {groups.map((group) => (
              <MenuItem
                key={group.accountId}
                onClick={() => {
                  setGroup(group);
                  setGroupSelector(null);
                }}
              >
                {group.name}
              </MenuItem>
            ))}
          </Menu>
          {view !== 'group' && (
            <Button
              disableElevation
              onClick={(evt) => setTeamSelector(evt.currentTarget)}
              endIcon={<KeyboardArrowDown />}
              ref={teamSelector}
              sx={{ width: '160px' }}
            >
              <Typography noWrap>{team?.title || 'Alle Teams'}</Typography>
            </Button>
          )}
          <Menu
            anchorEl={teamSelector}
            open={!!teamSelector}
            onClose={() => setTeamSelector(null)}
            onClick={(evt) => evt.stopPropagation()}
            sx={{ minWidth: '200px', minHeight: '200px' }}
          >
            <MenuItem
              onClick={() => {
                setTeam(null);
                setTeamSelector(null);
              }}
            >
              Alle Teams
            </MenuItem>
            {teams.map((team) => (
              <MenuItem
                key={team._id}
                onClick={() => {
                  setTeam(team);
                  setTeamSelector(null);
                }}
              >
                {team.title}
              </MenuItem>
            ))}
          </Menu>
          <Box sx={{ flex: 1 }} />
          <Stack direction="row" spacing={0}>
            <Button
              variant={view === 'userlist' ? 'contained' : 'outlined'}
              onClick={() => setView('userlist')}
              sx={{
                borderTopRightRadius: 0,
                borderBottomRightRadius: 0,
              }}
            >
              <ViewListOutlinedIcon />
            </Button>
            <Button
              variant={view === 'group' ? 'contained' : 'outlined'}
              onClick={() => setView('group')}
              sx={{
                borderRadius: 0,
              }}
            >
              <GroupsIcon />
            </Button>
            <Button
              variant={view === 'chart' ? 'contained' : 'outlined'}
              onClick={() => setView('chart')}
              sx={{
                borderTopLeftRadius: 0,
                borderBottomLeftRadius: 0,
              }}
            >
              <InsertChartOutlinedIcon />
            </Button>
          </Stack>
        </Card>

        {view !== 'group' && (
          <Box
            sx={{
              display: 'flex',
              flexFlow: 'row',
              gap: 2,
              width: '100%',
              justifyContent: 'space-between',
              mt: 2,
            }}
          >
            <Card
              sx={{ p: 2, flex: 1, position: 'relative' }}
              onClick={() => setFilter({ outgoing: false })}
            >
              {incoming !== null || loading ? (
                <Link to={`/crm/callcenter/calls`}>
                  <Typography variant="h3">{incoming || 0}</Typography>
                  <Typography variant="h6">eingehende Anrufe</Typography>
                </Link>
              ) : (
                <>
                  <Skeleton
                    variant="rectangular"
                    width={60}
                    height={60}
                    sx={{ borderRadius: 1 }}
                  />
                  <Skeleton
                    variant="rectangular"
                    height={25}
                    sx={{ width: '90%', mt: 1.2, borderRadius: 1 }}
                  />
                </>
              )}
              <Tooltip
                title="Eingehende Anrufe"
                fontSize="small"
                sx={{
                  position: 'absolute',
                  cursor: 'pointer',
                  top: 8,
                  right: 8,
                }}
              >
                <InfoOutlinedIcon color="disabled" />
              </Tooltip>
            </Card>
            <Card
              sx={{ p: 2, flex: 1, position: 'relative' }}
              onClick={() => setFilter({ outgoing: false, pickup: false })}
            >
              {missed !== null || loading ? (
                <Link to="/crm/callcenter/calls">
                  <Typography variant="h3">{missed || 0}</Typography>
                  <Typography variant="h6">verpasste Anrufe</Typography>
                </Link>
              ) : (
                <>
                  <Skeleton
                    variant="rectangular"
                    width={60}
                    height={60}
                    sx={{ borderRadius: 1 }}
                  />
                  <Skeleton
                    variant="rectangular"
                    height={25}
                    sx={{ width: '90%', mt: 1.2, borderRadius: 1 }}
                  />
                </>
              )}
              <Tooltip
                title="Alle eingegegangenen Anrufe, die nicht angenommen wurden."
                fontSize="small"
                sx={{
                  position: 'absolute',
                  cursor: 'pointer',
                  top: 8,
                  right: 8,
                }}
              >
                <InfoOutlinedIcon color="disabled" />
              </Tooltip>
            </Card>
            <Card
              sx={{ p: 2, flex: 1, position: 'relative' }}
              onClick={() => {
                setFilter({
                  outgoing: false,
                  pickup: true,
                });
                setSort({ waiting: -1 });
              }}
            >
              {waiting !== null || loading ? (
                <Link to="/crm/callcenter/calls">
                  <Typography variant="h3">{waiting?.toFixed(2)}s</Typography>
                  <Typography variant="h6">&#8960; Wartezeit</Typography>
                </Link>
              ) : (
                <>
                  <Skeleton
                    variant="rectangular"
                    width={60}
                    height={60}
                    sx={{ borderRadius: 1 }}
                  />
                  <Skeleton
                    variant="rectangular"
                    height={25}
                    sx={{ width: '90%', mt: 1.2, borderRadius: 1 }}
                  />
                </>
              )}
              <Tooltip
                title="Die Zeit in Sekunden, die ein Anruf im Wartezustand verbracht hat bis er angenommen oder beendet wurde."
                fontSize="small"
                sx={{
                  position: 'absolute',
                  cursor: 'pointer',
                  top: 8,
                  right: 8,
                }}
              >
                <InfoOutlinedIcon color="disabled" />
              </Tooltip>
            </Card>
            <Card
              sx={{ p: 2, flex: 1, position: 'relative' }}
              onClick={() => {
                setFilter({
                  outgoing: false,
                  pickup: false,
                });
                setSort({ waiting: -1 });
              }}
            >
              {waitingMissed !== null || loading ? (
                <Link to="/crm/callcenter/calls">
                  <Typography variant="h3">
                    {waitingMissed?.toFixed(2)}s
                  </Typography>
                  <Typography variant="h6">
                    &#8960; Geduld bis Abbruch
                  </Typography>
                </Link>
              ) : (
                <>
                  <Skeleton
                    variant="rectangular"
                    width={60}
                    height={60}
                    sx={{ borderRadius: 1 }}
                  />
                  <Skeleton
                    variant="rectangular"
                    height={25}
                    sx={{ width: '90%', mt: 1.2, borderRadius: 1 }}
                  />
                </>
              )}
              <Tooltip
                title="Die Zeit in Sekunden, die ein Anruf im Wartezustand war, bevor er abgebrochen wurde."
                fontSize="small"
                sx={{
                  position: 'absolute',
                  cursor: 'pointer',
                  top: 8,
                  right: 8,
                }}
              >
                <InfoOutlinedIcon color="disabled" />
              </Tooltip>
            </Card>
            {/* <Card sx={{ p: 2, flex: 1, position: 'relative' }}>
              {callbacks !== null || loading ? (
                <Link to="/organizer/callbacks">
                  <Typography variant="h3">{callbacks?.length}</Typography>
                  <Typography variant="h6">offene Rückrufe</Typography>
                </Link>
              ) : (
                <>
                  <Skeleton
                    variant="rectangular"
                    width={60}
                    height={60}
                    sx={{ borderRadius: 1 }}
                  />
                  <Skeleton
                    variant="rectangular"
                    height={25}
                    sx={{ width: '90%', mt: 1.2, borderRadius: 1 }}
                  />
                </>
              )}
              <Tooltip
                title="Rückrufe die für den ausgewählten Zeitraum offen waren"
                fontSize="small"
                sx={{
                  position: 'absolute',
                  cursor: 'pointer',
                  top: 8,
                  right: 8,
                }}
              >
                <InfoOutlinedIcon color="disabled" />
              </Tooltip>
            </Card> */}
            <Card
              sx={{ p: 2, flex: 1, position: 'relative' }}
              onClick={() => {
                setFilter({
                  outgoing: true,
                });
              }}
            >
              {outgoing !== null || loading ? (
                <Link to="/crm/callcenter/calls">
                  <Typography variant="h3">{outgoing}</Typography>
                  <Typography variant="h6">ausgehende Anrufe</Typography>
                </Link>
              ) : (
                <>
                  <Skeleton
                    variant="rectangular"
                    width={60}
                    height={60}
                    sx={{ borderRadius: 1 }}
                  />
                  <Skeleton
                    variant="rectangular"
                    height={25}
                    sx={{ width: '90%', mt: 1.2, borderRadius: 1 }}
                  />
                </>
              )}
              <Tooltip
                title="Ausgehende Anrufe"
                fontSize="small"
                sx={{
                  position: 'absolute',
                  cursor: 'pointer',
                  top: 8,
                  right: 8,
                }}
              >
                <InfoOutlinedIcon color="disabled" />
              </Tooltip>
            </Card>
          </Box>
        )}
      </Box>
      {view === 'userlist' && (
        <ListView
          rows={
            userStats?.filter((user) => {
              const matchesTeam = team
                ? team?.members.includes(user._id) ||
                  team?.managers.includes(user._id)
                : true;

              const filteredUsers = report?.data.users.filter((user) =>
                user.groups.includes(group?.name),
              );

              const matchesGroup = group
                ? filteredUsers.some((u) => u._id === user._id)
                : true;

              return matchesTeam && matchesGroup;
            }) || []
          }
          columns={columns}
        />
      )}
      {view === 'chart' && (
        <Box sx={{ mt: 2, p: 2 }}>
          <Card sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Anrufe
            </Typography>
            <BarChart
              width={1000}
              height={500}
              borderRadius={7}
              xAxis={
                isSingleDay
                  ? [{ scaleType: 'band', data: chartData.days, label: 'Tage' }]
                  : [
                      {
                        scaleType: 'band',
                        data: chartData.days,
                        label: isSingleDay ? 'Stunden' : 'Tage',
                      },
                    ]
              }
              series={[
                {
                  data: chartData.missed,
                  label: 'Verpasst',
                  color: theme.palette.error.main,
                },
                {
                  data: chartData.incoming,
                  label: 'Gesamt',
                  color: theme.palette.primary.main,
                },
              ]}
              legend={{ hidden: false }}
              stacked="true"
            />
          </Card>
        </Box>
      )}
      {view === 'group' && (
        <ListView
          rows={
            groupAnalysis?.map((group, index) => ({
              _id: `${group.accountId}_${index}`,
              ...group,
            })) || []
          }
          columns={groupColumns}
          detailComponents={[
            {
              Component: GroupCalllistDetails,
              title: 'Invoice Details',
              loading,
              additionalProps: { report },
            },
          ]}
        />
      )}
    </Box>
  );
}
